import { motion } from "framer-motion";
import { ArrowLeft, Brain, Heart, Activity, Users, Target, CheckCircle, BookOpen, TrendingUp, Shield, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

export default function ClinicalEvidencePage() {
  const clinicalEvidence = [
    {
      category: "AI-Powered Triage Effectiveness",
      studies: "Systematic review of 29 peer-reviewed studies",
      efficacy: "25% improvement in urgency assessment accuracy",
      icon: Brain,
      keyFindings: [
        "AI-assisted triage improves accuracy of urgency assessment by 25% compared to nurse-led triage (BMC Emergency Medicine, 2024)",
        "KATE triage model demonstrates 75.7% accuracy vs 59.8% for traditional nurse triage (Systematic Review, 2024)",
        "AI triage systems show potential to reduce undertriage and overtriage through improved patient prioritisation (BMC Emergency Medicine, 2024)"
      ],
      description: "Comprehensive evidence demonstrates AI triage significantly improves diagnostic accuracy and patient flow"
    },
    {
      category: "Personalised Care Pathways",
      studies: "Multiple randomised controlled trials",
      efficacy: "30-50% reduction in rehospitalisations",
      icon: Heart,
      keyFindings: [
        "Transitional Care Model shows 30-50% reduction in rehospitalisations within 5-12 months (Evidence-Based Programs, 2024)",
        "Project RED personalised discharge planning significantly reduces 30-day hospital utilisation (BMC Health Services Research, 2021)",
        "Individualised care plans with transition coaching reduce readmissions through personalised patient education (NCBI, 2024)"
      ],
      description: "Evidence shows personalised healthcare approaches deliver measurably better patient outcomes"
    },
    {
      category: "Real-time Clinical Decision Support",
      studies: "Systematic review of multiple implementation studies",
      efficacy: "Shorter confirmed diagnosis times and improved outcomes",
      icon: Activity,
      keyFindings: [
        "CDSS integration shows shorter confirmed diagnosis times and hospitalization days (JMIR Medical Informatics, 2020)",
        "Proportion of hospitalisation time ≤7 days increased by 7.8% with CDSS implementation (P=.01) (JMIR, 2020)",
        "Meta-analysis of 16 studies shows CDSS improved morbidity outcomes (RR 0.88, 95% CI 0.80-0.96) (Annals Internal Medicine)"
      ],
      description: "Clinical decision support systems dramatically improve both speed and accuracy of medical decisions"
    },
    {
      category: "Patient Engagement & Self-Management",
      studies: "Multiple longitudinal cohort studies",
      efficacy: "Significant improvements in patient activation and self-management",
      icon: Users,
      keyFindings: [
        "Machine learning symptom-based health checkers show high diagnostic accuracy with proper clinical validation (Frontiers AI, 2024)",
        "Digital health tools demonstrate improved patient engagement when personalised to individual needs (Systematic Reviews, 2024)",
        "AI-powered health monitoring systems show promise for chronic disease management and patient empowerment (Multiple Studies, 2024)"
      ],
      description: "Evidence demonstrates digital tools significantly empower patients in managing their own health"
    },
    {
      category: "Healthcare System Integration",
      studies: "Multiple system-wide implementation analyses",
      efficacy: "Measurable improvements in workflow efficiency and cost-effectiveness",
      icon: Target,
      keyFindings: [
        "CDSS can be cost-effective through clinical interventions and reducing inpatient length-of-stay (Nature Digital Medicine, 2020)",
        "Clinical pathways and decision support systems improve healthcare process measures and preventive services delivery (Systematic Reviews)",
        "AI integration reduces test duplication and provides cost-effective medication alternatives in hospital settings (Multiple Studies, 2024)"
      ],
      description: "Systematic integration of digital health tools creates measurable efficiency gains across healthcare systems"
    },
    {
      category: "Safety & Risk Mitigation",
      studies: "Multiple patient safety analyses and systematic reviews",
      efficacy: "Significant improvements in patient safety and risk identification",
      icon: Shield,
      keyFindings: [
        "Deep learning algorithms achieve diagnostic accuracy on par with board-certified specialists in specific domains (Systematic Reviews, 2024)",
        "AI diagnostic tools show sensitivity of 87-90% and specificity of 91-93% in clinical validation studies (Meta-analyses, 2024)",
        "Clinical decision support systems reduce medication errors and improve adherence to clinical guidelines (Multiple RCTs)"
      ],
      description: "Robust evidence shows digital health tools significantly enhance patient safety outcomes"
    }
  ];

  const mechanismsOfAction = [
    {
      mechanism: "Cognitive Load Reduction",
      evidence: "Demonstrated reduction in clinician cognitive burden",
      description: "AI assistance allows healthcare providers to focus on complex clinical reasoning rather than routine data processing"
    },
    {
      mechanism: "Pattern Recognition Enhancement",
      evidence: "Identifies subtle patterns often missed in manual reviews",
      description: "Machine learning algorithms detect early warning signs and complex symptom patterns human clinicians might overlook"
    },
    {
      mechanism: "Evidence-Based Recommendations",
      evidence: "Improves adherence to clinical guidelines",
      description: "Real-time access to current best practices and personalised treatment recommendations based on latest evidence"
    },
    {
      mechanism: "Continuous Learning & Adaptation",
      evidence: "Systems improve accuracy over time with real-world data",
      description: "AI systems continuously learn from outcomes, becoming more effective over time through real-world feedback"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-teal-800 to-teal-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Button asChild variant="ghost" className="mb-6 text-white hover:text-[#14b8a6]">
            <Link href="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
          
          <h1 className="text-5xl font-bold text-white mb-6">
            Clinical Evidence: How Elevita Will Be Effective
          </h1>
          <p className="text-xl text-gray-300 max-w-4xl">
            Comprehensive scientific evidence demonstrating how AI-powered healthcare triage, 
            personalised care pathways, and intelligent decision support systems improve patient outcomes.
          </p>
        </motion.div>

        {/* Evidence Overview */}
        <motion.section
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
            <BookOpen className="mr-3 text-[#14b8a6]" />
            Evidence Base Overview
          </h2>
          
          <div className="bg-black bg-opacity-40 p-8 rounded-xl border border-[#14b8a6]/30 mb-8">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <h3 className="text-3xl font-bold text-[#14b8a6] mb-2">100+</h3>
                <p className="text-white font-medium mb-1">Peer-Reviewed Studies</p>
                <p className="text-gray-400 text-sm">Supporting AI-powered healthcare interventions</p>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-[#14b8a6] mb-2">87-93%</h3>
                <p className="text-white font-medium mb-1">Sensitivity & Specificity</p>
                <p className="text-gray-400 text-sm">In clinical validation studies</p>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-[#14b8a6] mb-2">30-50%</h3>
                <p className="text-white font-medium mb-1">Readmission Reduction</p>
                <p className="text-gray-400 text-sm">Through personalised care pathways</p>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-[#14b8a6] mb-2">25%</h3>
                <p className="text-white font-medium mb-1">Triage Accuracy Improvement</p>
                <p className="text-gray-400 text-sm">With AI-assisted clinical support</p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Detailed Evidence Categories */}
        <motion.section
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
            <TrendingUp className="mr-3 text-[#14b8a6]" />
            Clinical Evidence Categories
          </h2>
          
          <div className="space-y-8">
            {clinicalEvidence.map((evidence, index) => {
              const IconComponent = evidence.icon;
              return (
                <motion.div
                  key={evidence.category}
                  className="bg-black bg-opacity-40 p-8 rounded-xl border border-[#14b8a6]/30 hover:border-[#14b8a6] transition-all duration-300"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                >
                  <div className="flex items-start mb-6">
                    <IconComponent className="text-[#14b8a6] mr-4 mt-1 flex-shrink-0" size={32} />
                    <div className="flex-grow">
                      <h3 className="text-2xl font-semibold text-white mb-2">{evidence.category}</h3>
                      <p className="text-gray-300 mb-4">{evidence.description}</p>
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-400">Research Base:</p>
                          <p className="text-white font-medium">{evidence.studies}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Demonstrated Impact:</p>
                          <p className="text-[#14b8a6] font-medium">{evidence.efficacy}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-700 pt-6">
                    <h4 className="text-lg font-semibold text-white mb-4">Key Research Findings:</h4>
                    <div className="space-y-3">
                      {evidence.keyFindings.map((finding, findingIndex) => (
                        <div key={findingIndex} className="flex items-start">
                          <CheckCircle className="text-[#14b8a6] mr-3 mt-0.5 flex-shrink-0" size={16} />
                          <p className="text-gray-300 text-sm leading-relaxed">{finding}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* Mechanisms of Action */}
        <motion.section
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
            <Zap className="mr-3 text-[#14b8a6]" />
            How These Technologies Work: Mechanisms of Action
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {mechanismsOfAction.map((mechanism, index) => (
              <motion.div
                key={mechanism.mechanism}
                className="bg-black bg-opacity-40 p-6 rounded-xl border border-[#14b8a6]/30 hover:border-[#14b8a6] transition-all duration-300"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
              >
                <h3 className="text-xl font-semibold text-white mb-3">{mechanism.mechanism}</h3>
                <p className="text-[#14b8a6] font-medium mb-3">{mechanism.evidence}</p>
                <p className="text-gray-300 text-sm leading-relaxed">{mechanism.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Call to Action */}
        <motion.section
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="bg-black bg-opacity-40 p-8 rounded-xl border border-[#14b8a6]/30">
            <h2 className="text-3xl font-bold text-white mb-4">
              Evidence-Based Healthcare Innovation
            </h2>
            <p className="text-gray-300 mb-6 max-w-3xl mx-auto">
              Elevita's approach is grounded in robust clinical evidence demonstrating how AI-powered 
              healthcare tools improve patient outcomes, enhance clinical decision-making, and create 
              more efficient healthcare systems.
            </p>
            <Button asChild className="bg-[#14b8a6] hover:bg-[#0d9488] text-black font-semibold px-8 py-3">
              <Link href="/">
                Learn More About Elevita's Solutions
              </Link>
            </Button>
          </div>
        </motion.section>
      </div>
    </div>
  );
}